/**
 * Задание stylepack/option-grid, коммит 2 — содержимое строки варианта со
 * значками: значок варианта (эмодзи или картинка) слева или справа от
 * подписи и общий значок справа у всех вариантов блока (стрелка, кружок со
 * стрелкой). Порядок в строке: отметка выбора (её ставит вызывающий рендер,
 * как решил вид строки), значок слева, подпись, значок справа варианта,
 * общий значок справа. Коммит 3 — место «сверху»: значок и подпись стопкой
 * по центру карточки (снимки Quittr), общий значок справа — вне стопки.
 *
 * Нет значка и нет общего значка справа — отдаются сами `children`: разметка
 * старой воронки та же байт в байт. Значки — украшение (`aria-hidden`):
 * экранный диктор читает подпись, как раньше.
 *
 * Общие три рендера строк вариантов: single_choice и multiple_choice
 * (PublicBlockRenderer.tsx) и multi_step_question (MultiStepQuestionRenderer).
 *
 * Задание stylepack/option-description — описание варианта (`description`,
 * его передают только рендеры single_choice и multiple_choice): второй
 * строкой под подписью в той же колонке текста, значки и отметка выбора на
 * своих местах. Нет описания — разметка прежняя.
 *
 * Задание stylepack/option-label-hidden — поле блока `optionLabelHidden`:
 * у варианта со значком подпись и описание только для читалки (доступное имя
 * прежнее), значок по центру карточки при любом месте значка. Вариант без
 * значка показывает подпись; нет поля — разметка прежняя.
 */
import React from 'react';
import { pickOnPrimaryColor } from '@/themes/colorUtils';
import { opaqueContrastBaseOf } from '@/themes/surfaceOpacity';
import {
  OPTION_LABEL_VISUALLY_HIDDEN,
  optionDescriptionStyle,
  optionIconPositionOf,
  optionLabelHiddenOf,
  optionIconSizeOf,
  optionTrailingIconColorOf,
  optionTrailingIconOf,
  validOptionIcon,
  type OptionDescriptionFields,
  type OptionIconFields,
} from './optionGrid';
import { optionContentGapOverride, type OptionRowFields } from './optionRowStyle';

interface Props {
  fields: OptionIconFields &
    OptionDescriptionFields &
    Pick<OptionRowFields, 'optionContentGap'>;
  icon: unknown;
  /** Описание варианта для показа; нет или пусто — строки нет. */
  description?: React.ReactNode;
  /**
   * Пак с текстом варианта по центру (`type.optionAlign: 'center'`,
   * outline-pills, WEB-3718): подпись у значка сбоку держит выравнивание
   * из переменной пака. Нет — разметка прежняя.
   */
  centered?: boolean;
  children: React.ReactNode;
}

function LeadingIcon({
  icon,
  size,
}: {
  icon: NonNullable<ReturnType<typeof validOptionIcon>>;
  size: number;
}) {
  if (icon.kind === 'emoji') {
    return (
      <span
        aria-hidden="true"
        data-testid="option-icon"
        style={{ fontSize: size, lineHeight: 1, flex: '0 0 auto' }}
      >
        {icon.value}
      </span>
    );
  }
  return (
    <img
      src={icon.url}
      alt=""
      aria-hidden="true"
      data-testid="option-icon"
      style={{ width: size, height: size, objectFit: 'contain', flex: '0 0 auto' }}
    />
  );
}

function TrailingIcon({ fields }: { fields: OptionIconFields }) {
  const kind = optionTrailingIconOf(fields);
  if (kind === 'none') return null;
  const size = optionIconSizeOf(fields);
  const own = optionTrailingIconColorOf(fields);
  // Стрелку на своём кружке считаем по кружку только сквозь общий порог
  // непрозрачности (WEB-1859): у полупрозрачного кружка (#rrggbbaa) судить
  // нечем — цвет темы (сведение стильпаков, WEB-3718).
  const base = own ? opaqueContrastBaseOf(own) : null;
  const common = {
    'aria-hidden': true,
    'data-testid': 'option-trailing-icon',
    'data-kind': kind,
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    style: { flex: '0 0 auto', marginLeft: 'auto' },
  } as const;
  const arrow = (stroke: string) => (
    <path
      d="M6 12h12M13 7l5 5-5 5"
      fill="none"
      stroke={stroke}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  );
  if (kind === 'arrow') {
    return <svg {...common}>{arrow(own ?? 'currentColor')}</svg>;
  }
  return (
    <svg {...common}>
      <circle cx={12} cy={12} r={12} fill={own ?? 'var(--color-primary, #1677ff)'} />
      {arrow(base ? pickOnPrimaryColor(base) : 'var(--color-on-primary, #fff)')}
    </svg>
  );
}

const ICON_TEXT_ALIGN: React.CSSProperties = {
  textAlign: 'var(--option-icon-text-align)' as React.CSSProperties['textAlign'],
};

export function OptionRowContent({
  fields,
  icon,
  description,
  centered,
  children,
}: Props) {
  const own = validOptionIcon(icon);
  const trailing = optionTrailingIconOf(fields) !== 'none';
  const described =
    description !== undefined && description !== null && description !== '';
  if (!own && !trailing && !described) return <>{children}</>;
  const size = optionIconSizeOf(fields);
  const position = optionIconPositionOf(fields);
  // Задание stylepack/option-label-hidden: подпись (с описанием) только для
  // читалки — у варианта с годным значком.
  const hidden = optionLabelHiddenOf(fields, icon);
  const labelWith = (align: React.CSSProperties = {}) => (
    <span
      data-testid="option-label"
      style={
        hidden
          ? OPTION_LABEL_VISUALLY_HIDDEN
          : { flex: '1 1 auto', minWidth: 0, ...align }
      }
    >
      {children}
      {described && (
        <span
          data-testid="option-description"
          style={optionDescriptionStyle(fields)}
        >
          {description}
        </span>
      )}
    </span>
  );
  const label = labelWith();
  // Значок по центру карточки при любом месте значка; общий значок справа —
  // на своём месте.
  if (own && hidden) {
    return (
      <>
        <span
          data-testid="option-icon-center"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: '1 1 auto',
            minWidth: 0,
          }}
        >
          <LeadingIcon icon={own} size={size} />
        </span>
        {label}
        <TrailingIcon fields={fields} />
      </>
    );
  }
  if (own && position === 'top') {
    return (
      <>
        <span
          data-testid="option-stack"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 8,
            // Задание stylepack/option-content-gap: тот же зазор значка и
            // текста, что у строки; пусто — {}, прежние 8.
            ...optionContentGapOverride(fields),
            flex: '1 1 auto',
            minWidth: 0,
          }}
        >
          <LeadingIcon icon={own} size={size} />
          {label}
        </span>
        <TrailingIcon fields={fields} />
      </>
    );
  }
  const right = position === 'right';
  // Пак с текстом по центру (outline-pills, WEB-3718): подпись у значка слева —
  // у значка (переменная пака), а не по центру строки.
  const besideIcon = own && !right && centered;
  return (
    <>
      {own && !right && <LeadingIcon icon={own} size={size} />}
      {besideIcon ? labelWith(ICON_TEXT_ALIGN) : label}
      {own && right && <LeadingIcon icon={own} size={size} />}
      <TrailingIcon fields={fields} />
    </>
  );
}
